import { TINYMCE_EDITOR_LICENSE_KEY } from 'config';
import EditorMentions, { ISingleSelectOption } from './EditorMentions';
import { InitOptions } from '@tinymce/tinymce-react/lib/cjs/main/ts/components/Editor';
import tinymce, { Editor as IEditor } from 'tinymce';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHideOnClickOutside } from 'hooks/useHideOnClickOutside';
import { Editor as TinyMceEDitor } from '@tinymce/tinymce-react';
import { theme } from '../../../tailwind.config';
import { isHtml } from 'utils/string';
import { optionToHtml } from './helpers';

export interface IMentionOption {
  name: string;
  email: string;
  role: string;
  id: string;
}

export interface IEditorProps {
  handleEditorChange: (content: any, editor: any) => void;
  text: string;
  options: IMentionOption[];
  initVals?: InitOptions;
  mentionAbility?: boolean;
  minHeight?: string;
  maxHeight?: string;
  flex?: string;
  placeHolder?: string;
  disabled?: boolean;

  // this is because if you start with plain text, the editor will fire its onEditorChange event when the instance
  // mounts, because its changing the plain text to HTML. But we don't actually always want that, because it
  // triggers stuff like our "Leave without saving" modal.
  // So instead, if this is true, we'll use the isInitialLoad ref below to skip the first onEditorChange event
  // HOWEVER, if the initial text is HTML, we DONT want to skip, so if we detect that, we force skipInitialChange to false
  skipInitialChange?: boolean;
}

const Editor = ({
  handleEditorChange,
  text,
  options = [],
  initVals,
  mentionAbility = true,
  disabled = false,
  placeHolder,
  minHeight = '200px',
  maxHeight = undefined,
  flex,
  skipInitialChange = false,
}: IEditorProps) => {
  const editorRef = useRef<IEditor | null>(null);
  const isInitialLoad = useRef<boolean>(true);
  const [nameFilter, setNameFilter] = useState('');
  const [mentionPosition, setMentionPosition] = useState<{ x: number; y: number } | null>(null);
  const [isMentionListOpen, setIsMentionListOpen] = useState<boolean>(false);
  const { instanceClassname } = useHideOnClickOutside(setIsMentionListOpen, 'mentions-list');

  useEffect(() => {
    const str = editorRef.current?.selection.getRng().endContainer.textContent?.trim() || '';
    if (str?.[0] === '@') {
      setNameFilter(str.substring(1));
    }
  }, [isMentionListOpen, text]);

  const handleInitEditor = useCallback((_evt, editor) => (editorRef.current = editor), []);

  const handleEditorChangeWrapper = useCallback(
    (content: any, editor: any) => {
      // If the initial text is HTML, we never want to skip the initial change
      const shouldSkip = skipInitialChange && !isHtml(text) && isInitialLoad.current;

      if (shouldSkip) {
        isInitialLoad.current = false;
        return;
      }
      isInitialLoad.current = false;
      handleEditorChange(content, editor);
    },
    [handleEditorChange, skipInitialChange, text]
  );

  const handleMentionPosition = useCallback((editor: IEditor) => {
    setNameFilter('');
    editor.formatter.apply('forecolor', { value: theme.colors['bright-sky-blue'] });
    editor.selection.collapse(false);

    const selection = editor.selection;
    const range = selection.getRng();

    if (!range.collapsed) {
      return null;
    }

    const span = document.createElement('span');
    span.appendChild(document.createTextNode('\u200B')); // Zero-width space character
    range.insertNode(span);

    try {
      const rect = span.getBoundingClientRect();
      span.parentNode?.removeChild(span);

      setMentionPosition({
        x: rect.left,
        y: rect.top + 68,
      });
    } catch (error) {
      console.error(error);
    }
  }, []);

  const handleSelectMention = useCallback((option: ISingleSelectOption) => {
    const editor = editorRef.current;
    if (!editor) {
      return;
    }

    const selection = editor.selection;
    const range = selection.getRng();

    // Get the current node and its text content
    const currentNode = range.startContainer;
    const nodeText = currentNode.textContent || '';
    const cursorOffset = range.startOffset;

    // Find the last "@" symbol before the cursor in the current text node
    const beforeCursor = nodeText.substring(0, cursorOffset);
    const lastAtSignIndex = beforeCursor.lastIndexOf('@');

    if (lastAtSignIndex !== -1) {
      try {
        // Create a new range from the "@" symbol to the current cursor position
        const newRange = editor.dom.createRng();
        newRange.setStart(currentNode, lastAtSignIndex);
        newRange.setEnd(currentNode, cursorOffset);

        // Set the selection to this range
        selection.setRng(newRange);

        // Remove the forecolor formatting
        editor.formatter.remove('forecolor', { value: theme.colors['bright-sky-blue'] });

        // Delete the selected content (from "@" to cursor)
        selection.setContent('');

        // Insert the new mention HTML
        selection.setContent(optionToHtml(option.id, option.name, option.email), { format: 'html' });
      } catch (error) {
        // Fallback: just insert at current position if no "@" found
        selection.setContent(optionToHtml(option.id, option.name, option.email), { format: 'html' });
      }
    } else {
      // Fallback: just insert at current position if no "@" found
      selection.setContent(optionToHtml(option.id, option.name, option.email), { format: 'html' });
    }

    setNameFilter('');
    setIsMentionListOpen(false);
    editor.focus();
  }, []);

  const handleOnKeyDown = useCallback(
    e => {
      if (e.key === '@' && editorRef.current && mentionAbility) {
        setIsMentionListOpen(true);
        handleMentionPosition(editorRef.current);
      } else if (e.key === ' ' && isMentionListOpen && mentionAbility) {
        setIsMentionListOpen(false);
        setNameFilter('');
        editorRef.current?.formatter.remove('forecolor', undefined, editorRef.current?.selection.getRng());
        editorRef.current?.selection.collapse(false);
      } else if (e.key === 'Backspace' && nameFilter === '' && isMentionListOpen && mentionAbility) {
        setIsMentionListOpen(false);
        editorRef.current?.formatter.remove('forecolor', undefined, editorRef.current?.selection.getRng());
      }
    },
    [nameFilter, isMentionListOpen]
  );

  const editorInitVals = useMemo(
    () => ({
      menubar: false,
      branding: false,
      plugins: 'autoresize lists textcolor',
      elementpath: false,
      toolbar:
        'blocks | bold italic | forecolor | alignleft aligncenter alignright alignjustify | outdent indent | numlist bullist',
      content_style: `body { font-family:Helvetica,Arial,sans-serif; font-size:14px; color: ${theme.colors['black']} } .mce-content-body[data-mce-placeholder]:not(.mce-visualblocks)::before {font-style: italic; color: ${theme.colors['gray-100']}}`,
      noneditable_class: 'non-editable',
      placeholder: placeHolder ?? '',
      resize: true as boolean | 'both' | undefined,
      color_map: [
        '#413E3B', 'Dark Gray',
        '#A08264', 'Brown', 
        '#70D6BA', 'Teal',
        '#F49C20', 'Orange',
        '#D4244E', 'Red',
        '#968F8B', 'Gray',
        '#D9CDC1', 'Light Beige',
        '#C6EFE3', 'Light Mint',
        '#FAD03E', 'Yellow',
        '#FD4B4C', 'Bright Red'
      ],
      setup: (editor: IEditor) => {
        editor.on('init', () => {
          const statusBar = editor.getContainer().querySelector('.tox-statusbar') as HTMLElement | null;
          if (statusBar) {
            statusBar.style.justifyContent = 'end';
          }
          editor.getContainer().style.minHeight = minHeight;
          if (maxHeight) {
            editor.getContainer().style.maxHeight = maxHeight;
          }
          if (flex) {
            editor.getContainer().style.flex = flex;
          }
        });
      },
      ...(initVals || {}),
    }),
    []
  );

  const editorStyles = useMemo<React.CSSProperties>(
    () => ({
      position: 'absolute',
      left: `${mentionPosition?.x}px`,
      top: `${mentionPosition?.y}px`,
      zIndex: '2000',
    }),
    [mentionPosition]
  );

  return (
    <>
      {isMentionListOpen && (
        <EditorMentions
          options={options}
          style={editorStyles}
          nameFilter={nameFilter}
          onChange={handleSelectMention}
          className={instanceClassname}
        />
      )}
      <TinyMceEDitor
        tinymceScriptSrc="/tinymce/tinymce.min.js"
        licenseKey={TINYMCE_EDITOR_LICENSE_KEY}
        onInit={handleInitEditor}
        onEditorChange={handleEditorChangeWrapper}
        value={text}
        init={editorInitVals}
        onKeyDown={handleOnKeyDown}
        disabled={disabled}
      />
    </>
  );
};

export default Editor;
